/* agmf-pref.js — who is looking, in the two terms the feed can act on:
   the languages their country speaks, and the colour they favour.
   ─────────────────────────────────────────────────────────────────────────
   Dorian, 2026-09-03, on a German review reaching a French browser:

     "Start wiring the Country to give priority to the content tagging its
      languages. All spanish talking countries should show the content tagged
      as spanish etc in priority (harmonize it dont just put all straight but
      it should along with the international content)."
     "Also if we have someone's favorite color that is Black or Coyote Brown,
      etc, prioritize the content tagged in AM as their favorite color."

   THIS FILE DECIDES NOTHING. It answers two questions - what does this
   visitor prefer, and how does a given piece rank against that - and the feed
   engine asks. Kept apart because the engine's ordering has invariants of its
   own (a strict kind rotation, per-platform caps, never two moving cards on a
   row) and a preference must bend the order WITHIN those, never around them.

   WHERE THE LANGUAGES COME FROM, strongest first:
     1. the locale this page is being read in, when it is not the store's
        default - an act, here, now;
     2. `navigator.languages` - what this machine says it reads, which is the
        only one of the three that is about a person AND present for everyone;
     3. the languages of the shopper's country - an inference about a place.
   All three, deduped and in that order. English is never in this list from
   any source; it is the shelf, not somebody's preference.

   HARMONISED, NOT FRONT-LOADED. `blend()` deals a lane alternately from the
   matches and from the international pile, so a French visitor sees French
   content early AND the English content that most of the library is, rather
   than four French clips in a row and everything else after. A language that
   is neither theirs nor international sinks to the back of its lane - which
   is the case he was looking at.

   WHAT COUNTS AS INTERNATIONAL: English, and anything the library could not
   name (26 of 123 items on the belt today). Neither is a foreign language to
   anybody, and both are the bulk of the shelf.

   ⚠ TWO THINGS THIS CANNOT FIX, both upstream in Agilite Media:
   1. The German review that started this carries `language: "en"` in the
      feed (ck yt:zN5hFhUIAYo, channel "Spätzle Operator", title in German).
      A wrong label cannot be out-ranked by a correct rule - it will read as
      international until the library re-detects it.
   2. No feed item carries a colour today (0 of 123; the product card's own
      deck does, 6 of 17). The colour side below reads `colours`, `colour` and
      colour-looking `tags`, so it starts working the day the exporter ships
      them - and scores nothing until then.
   ───────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';
  if (window.AGMFPref) return;

  /* ── country → the languages spoken there ──────────────────────────────
     Only what a shopper would recognise as "content in my language". English
     is deliberately absent as a preference: it is the international pile, and
     an English-speaking country therefore prefers nothing in particular,
     which is the correct answer for it. */
  var LANGS = {
    // Spanish, every country of it (Dorian asked for exactly this)
    ES: ['es'], MX: ['es'], AR: ['es'], CO: ['es'], PE: ['es'], CL: ['es'],
    VE: ['es'], EC: ['es'], GT: ['es'], CU: ['es'], BO: ['es'], DO: ['es'],
    HN: ['es'], PY: ['es'], SV: ['es'], NI: ['es'], CR: ['es'], PA: ['es'],
    UY: ['es'], PR: ['es'], GQ: ['es', 'fr'],
    // French
    FR: ['fr'], MC: ['fr'], SN: ['fr'], CI: ['fr'], ML: ['fr'], BF: ['fr'],
    NE: ['fr'], TG: ['fr'], BJ: ['fr'], CD: ['fr'], CG: ['fr'], GA: ['fr'],
    MG: ['fr'], HT: ['fr'], CM: ['fr'],
    // German
    DE: ['de'], AT: ['de'], LI: ['de'],
    // shared
    CH: ['de', 'fr', 'it'], BE: ['nl', 'fr'], LU: ['fr', 'de'], CA: ['fr'],
    // Portuguese, Italian, Dutch
    PT: ['pt'], BR: ['pt'], AO: ['pt'], MZ: ['pt'],
    IT: ['it'], SM: ['it'], VA: ['it'], NL: ['nl'],
    // central + eastern Europe
    PL: ['pl'], CZ: ['cs'], SK: ['sk'], HU: ['hu'], RO: ['ro'], MD: ['ro'],
    RS: ['sr'], BA: ['sr', 'hr'], ME: ['sr'], HR: ['hr'], SI: ['sl'],
    BG: ['bg'], MK: ['mk'], AL: ['sq'], GR: ['el'], CY: ['el', 'tr'],
    RU: ['ru'], BY: ['ru'], KZ: ['ru', 'kk'], UA: ['uk', 'ru'],
    LT: ['lt'], LV: ['lv'], EE: ['et'],
    // Nordics
    SE: ['sv'], NO: ['no'], DK: ['da'], FI: ['fi', 'sv'], IS: ['is'],
    // the Middle East + Türkiye
    IL: ['he'], TR: ['tr'],
    SA: ['ar'], AE: ['ar'], EG: ['ar'], JO: ['ar'], QA: ['ar'], KW: ['ar'],
    BH: ['ar'], OM: ['ar'], LB: ['ar'], IQ: ['ar'], LY: ['ar'], SD: ['ar'],
    YE: ['ar'], SY: ['ar'], MA: ['ar', 'fr'], DZ: ['ar', 'fr'], TN: ['ar', 'fr'],
    // Asia + the rest
    JP: ['ja'], KR: ['ko'], CN: ['zh'], TW: ['zh'], HK: ['zh'], SG: ['zh'],
    TH: ['th'], VN: ['vi'], ID: ['id'], MY: ['ms'], PH: ['tl'],
    IN: ['hi'], PK: ['ur'], BD: ['bn'], NP: ['ne'], LK: ['si'],
    ET: ['am'], KE: ['sw'], TZ: ['sw'], NG: [], ZA: [], GH: [],
    // English-first: no preference, which is the right answer
    US: [], GB: [], AU: [], NZ: [], IE: [], JM: []
  };

  // The geo app on this storefront stores a country NAME, not a code
  // (localStorage.country = "United States"), so both are accepted.
  var NAMES = {
    'united states': 'US', 'usa': 'US', 'united kingdom': 'GB', 'great britain': 'GB',
    'england': 'GB', 'scotland': 'GB', 'wales': 'GB', 'ireland': 'IE',
    france: 'FR', germany: 'DE', austria: 'AT', switzerland: 'CH', belgium: 'BE',
    netherlands: 'NL', holland: 'NL', luxembourg: 'LU', monaco: 'MC',
    spain: 'ES', mexico: 'MX', argentina: 'AR', colombia: 'CO', peru: 'PE',
    chile: 'CL', venezuela: 'VE', ecuador: 'EC', guatemala: 'GT', cuba: 'CU',
    bolivia: 'BO', 'dominican republic': 'DO', honduras: 'HN', paraguay: 'PY',
    'el salvador': 'SV', nicaragua: 'NI', 'costa rica': 'CR', panama: 'PA',
    uruguay: 'UY', 'puerto rico': 'PR',
    portugal: 'PT', brazil: 'BR', angola: 'AO', mozambique: 'MZ',
    italy: 'IT', 'san marino': 'SM', greece: 'GR', cyprus: 'CY',
    poland: 'PL', czechia: 'CZ', 'czech republic': 'CZ', slovakia: 'SK',
    hungary: 'HU', romania: 'RO', moldova: 'MD', serbia: 'RS', croatia: 'HR',
    'bosnia and herzegovina': 'BA', montenegro: 'ME', slovenia: 'SI',
    bulgaria: 'BG', 'north macedonia': 'MK', albania: 'AL',
    russia: 'RU', 'russian federation': 'RU', belarus: 'BY', kazakhstan: 'KZ',
    ukraine: 'UA', lithuania: 'LT', latvia: 'LV', estonia: 'EE',
    sweden: 'SE', norway: 'NO', denmark: 'DK', finland: 'FI', iceland: 'IS',
    israel: 'IL', 'türkiye': 'TR', turkey: 'TR',
    'saudi arabia': 'SA', 'united arab emirates': 'AE', egypt: 'EG', jordan: 'JO',
    qatar: 'QA', kuwait: 'KW', bahrain: 'BH', oman: 'OM', lebanon: 'LB',
    iraq: 'IQ', libya: 'LY', morocco: 'MA', algeria: 'DZ', tunisia: 'TN',
    japan: 'JP', 'south korea': 'KR', korea: 'KR', china: 'CN', taiwan: 'TW',
    'hong kong': 'HK', singapore: 'SG', thailand: 'TH', vietnam: 'VN',
    indonesia: 'ID', malaysia: 'MY', philippines: 'PH', india: 'IN',
    pakistan: 'PK', bangladesh: 'BD', nepal: 'NP', 'sri lanka': 'LK',
    canada: 'CA', australia: 'AU', 'new zealand': 'NZ',
    'south africa': 'ZA', nigeria: 'NG', kenya: 'KE', tanzania: 'TZ',
    ghana: 'GH', ethiopia: 'ET', jamaica: 'JM'
  };

  var INTERNATIONAL = { en: 1, unknown: 1, '': 1 };

  function readCountry() {
    var raw = null;
    try { raw = localStorage.getItem('country') || localStorage.getItem('Country'); } catch (e) {}
    if (raw) {
      var s = String(raw).trim();
      if (/^[A-Za-z]{2}$/.test(s)) return s.toUpperCase();
      var code = NAMES[s.toLowerCase()];
      if (code) return code;
    }
    try { if (window.Shopify && Shopify.country) return String(Shopify.country).toUpperCase(); } catch (e) {}
    return null;
  }

  /* ── the language the page is being READ in, when that is a statement ──
     Shopify serves a store's DEFAULT locale at the root and every other
     published one under a prefix, so `Shopify.routes.root !== '/'` means this
     shopper is not on the default: they switched languages, or their browser
     asked for one and Shopify honoured it. Either way it is a fact about a
     PERSON, where a country is only ever an inference about a place - so it
     goes in front of the country's languages rather than beside them.

     Measured 2026-09-07, so this is not theoretical: ROW publishes en/de/fr/es,
     EU en/es, IL he/en. The signal is real on all three.

     Two reasons it does NOT replace the country table:
       - it is SILENT for everyone still on the default locale, which is most
         people, and silence is not the same as "no preference";
       - the storefronts publish four languages while the library holds many
         more (Thai, for one), so the locale can never ask for content in a
         language the shop does not sell in.
     English is skipped on purpose even when it IS a switch (IL's /en): the
     rank below treats English as the international shelf, never as somebody's
     preference, and this must not quietly turn that into one. */
  function readLocale() {
    try {
      var S = window.Shopify || {};
      var root = (S.routes && S.routes.root) || '/';
      if (root === '/' || !S.locale) return null;
      var code = String(S.locale).trim().toLowerCase().split('-')[0];
      return code && !INTERNATIONAL[code] ? code : null;
    } catch (e) { return null; }
  }

  /* ── the languages this DEVICE says it reads ───────────────────────────
     `navigator.languages` is the ordered list the shopper (or whoever set the
     machine up) put in their OS and browser settings. It is the only signal
     of the three that is both about a PERSON and present for everyone: the
     locale is silent for the majority who never leave the default, and a
     country is a guess about a place. It is what reaches the French speaker
     in the United States who never touches the language switcher - the case
     neither of the other two can see.

     Weaker than an active switch, stronger than a country: somebody reading
     the German storefront right now is saying something more immediate than a
     preference list configured once, and both beat "people in this country
     tend to speak X".

     Every tag is cut to its base ("fr-CA" -> "fr"), which also collapses the
     regional duplicates most devices list. No cap: a machine listing four
     languages belongs to somebody who reads four, and merge() drops English
     anyway - so the usual list of ["en-US", "fr-FR", "fr"] contributes
     exactly one language. */
  function readBrowserLangs() {
    var out = [];
    try {
      var nav = window.navigator || {};
      var list = (nav.languages && nav.languages.length) ? nav.languages
        : (nav.language ? [nav.language] : []);
      for (var i = 0; i < list.length; i++) {
        var code = String(list[i] || '').trim().toLowerCase().split('-')[0];
        if (code) out.push(code);
      }
    } catch (e) {}
    return out;
  }

  /* One list, in order of how much each source actually knows, with English
     and repeats dropped whichever source offered them. */
  function merge() {
    var out = [], seen = {};
    for (var i = 0; i < arguments.length; i++) {
      var list = arguments[i] || [];
      for (var j = 0; j < list.length; j++) {
        var s = String(list[j] || '').trim().toLowerCase();
        if (!s || seen[s] || INTERNATIONAL[s]) continue;
        seen[s] = 1;
        out.push(s);
      }
    }
    return out;
  }

  function readColours() {
    var out = [];
    try {
      var p = window.SE_colorPref;
      if (p && p.colors && p.colors.length) out = p.colors.slice(0);
      else {
        var raw = localStorage.getItem('SE_colorPref');
        if (raw) { var j = JSON.parse(raw); if (j && j.colors) out = j.colors.slice(0); }
      }
    } catch (e) {}
    return out.map(function (c) { return String(c || '').trim().toLowerCase(); }).filter(Boolean);
  }

  // `live` is the colourway the shopper has SELECTED on this page. It beats
  // `colours`, which is what the personalisation service thinks they like
  // from past visits: a finger on the Multicam swatch is a stronger and more
  // current statement than any guess, and while they are looking at Black,
  // Multicam content is not what they want ranked up.
  var state = { country: null, locale: null, browser: [], langs: [], colours: [], live: [] };
  function refresh() {
    state.country = readCountry();
    state.locale = readLocale();
    state.browser = readBrowserLangs();
    // STRONGEST FIRST: what they chose here, what their machine says they
    // read, then what their country tends to speak.
    state.langs = merge(
      state.locale ? [state.locale] : [],
      state.browser,
      (state.country && LANGS[state.country]) ? LANGS[state.country] : []);
    state.colours = readColours();
    return state;
  }
  refresh();
  // the colour list is fetched per visitor and can land after this file runs
  window.addEventListener('SE_colorPrefReady', function () {
    refresh();
    try { window.dispatchEvent(new CustomEvent('agmf:prefchanged', { detail: { colours: state.colours } })); } catch (e) {}
  });

  function langOf(item) {
    return String((item && item.language) || '').trim().toLowerCase();
  }

  // A piece's colours, wherever the library ends up putting them: the hero
  // deck already uses `colours`, and a tag is the other honest place.
  function coloursOf(item) {
    var out = [];
    if (!item) return out;
    if (item.colours && item.colours.length) out = out.concat(item.colours);
    if (item.colour) out.push(item.colour);
    if (item.color) out.push(item.color);
    if (item.tags && item.tags.length) out = out.concat(item.tags);
    return out.map(function (c) { return String(c || '').trim().toLowerCase(); }).filter(Boolean);
  }

  // What colour we are ranking by right now: the selected variant if there
  // is one, otherwise what they are known to favour.
  function wanted() {
    return state.live.length ? state.live : state.colours;
  }

  function colourMatch(item) {
    var want = wanted();
    if (!want.length) return false;
    var mine = coloursOf(item);
    if (!mine.length) return false;
    for (var i = 0; i < want.length; i++) {
      if (mine.indexOf(want[i]) !== -1) return true;
    }
    return false;
  }

  // 'preferred' — speaks their language, or wears their colour
  // 'neutral'   — international: English, or a language the library could not name
  // 'foreign'   — a named language that is neither theirs nor international
  function rank(item) {
    var l = langOf(item);
    if (state.langs.length && l && state.langs.indexOf(l) !== -1) return 'preferred';
    if (colourMatch(item)) return 'preferred';
    if (!l || INTERNATIONAL[l]) return 'neutral';
    return 'foreign';
  }

  // Deal the matches and the international pile ALTERNATELY, foreign last.
  // Stable within each pile, so the library's own arrangement survives - this
  // only decides which of two equally-placed pieces comes first.
  function blend(list) {
    if (!list || list.length < 3) return list || [];
    if (!state.langs.length && !wanted().length) return list;        // nothing to prefer
    var pref = [], neutral = [], foreign = [];
    for (var i = 0; i < list.length; i++) {
      var r = rank(list[i]);
      (r === 'preferred' ? pref : r === 'foreign' ? foreign : neutral).push(list[i]);
    }
    if (!pref.length && !foreign.length) return list;                // nothing moved
    var out = [], a = 0, b = 0;
    while (a < pref.length || b < neutral.length) {
      if (a < pref.length) out.push(pref[a++]);
      if (b < neutral.length) out.push(neutral[b++]);
    }
    return out.concat(foreign);
  }

  window.AGMFPref = {
    country: function () { return state.country; },
    /* The non-default locale this page is being read in, or null on the
       default - which is the honest answer, not "English". */
    locale: function () { return state.locale; },
    /* What the device asked for, base codes, English included - the raw
       reading, before merge() drops the shelf languages. */
    browser: function () { return state.browser.slice(0); },
    /* What this SHOPPER stated - the locale they are reading in, and what
       their device asks for - with the country's inference left out. The feed
       filter uses this to decide what `englishCountries` is allowed to clear:
       that list is a claim about a PLACE, so it may overrule the country's own
       guess and nothing else. Without the distinction a French speaker in the
       United States was cut back to English-only, which is the exact person
       navigator.languages was added to reach. */
    stated: function () {
      return merge(state.locale ? [state.locale] : [], state.browser);
    },
    langs: function () { return state.langs.slice(0); },
    // What is being ranked by, which is the selected colourway when there is
    // one. `stored()` is still the visitor's own preference, untouched.
    colours: function () { return wanted().slice(0); },
    stored: function () { return state.colours.slice(0); },
    /* The colourway the shopper has selected on this page, from
       agmf-variant-pref.js. An empty list gives the stored preference back. */
    prefer: function (list) {
      state.live = (list || []).map(function (c) {
        return String(c || '').trim().toLowerCase();
      }).filter(Boolean);
      return state.live.slice(0);
    },
    rank: rank,
    blend: blend,
    refresh: refresh,
    /* THE TABLE ARRIVES IN THE FEED. Agilite Media owns the list
       (src/data/country-languages.json) and ships it, so there is one list
       instead of a copy on each side - they had already drifted apart on
       Switzerland, Belgium, Luxembourg and Canada. The literal above stays as
       the fallback for a feed exported before this shipped. */
    useTable: function (table) {
      if (!table || typeof table !== 'object') return false;
      var next = {}, count = 0;
      for (var code in table) {
        if (!Object.prototype.hasOwnProperty.call(table, code)) continue;
        if (!/^[A-Za-z]{2}$/.test(code) || !Array.isArray(table[code])) continue;
        next[code.toUpperCase()] = table[code].map(function (l) {
          return String(l || '').trim().toLowerCase();
        }).filter(Boolean);
        count++;
      }
      if (!count) return false;
      LANGS = next;
      refresh();
      return true;
    },
    _table: function () { return LANGS; }
  };
}());
